import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, IdCard, Mail, MapPin, Pencil, Phone, UserRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { mapsSearch, telHref } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";
import { ClientFields, blankClient } from "@/components/app/ClientFields";
import { WhatsAppButton } from "@/components/app/WhatsAppButton";

const Line = ({ icon: Icon, testid, value, empty, href, external }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    {!value ? <span className="text-slate-500">{empty}</span>
      : href ? <a href={href} target={external ? "_blank" : undefined} rel="noreferrer" className="break-all hover:underline">{value}</a>
        : <span className="whitespace-pre-line break-words">{value}</span>}
  </div>
);

const ContactView = ({ c, onEdit }) => (
  <div className="flex items-start gap-3">
    <div className="grid min-w-0 flex-1 gap-2 text-sm sm:grid-cols-2">
      <Line icon={Phone} testid="client-phone" value={c.phone} empty="Sem telefone" href={c.phone && telHref(c.phone)} />
      <Line icon={Mail} testid="client-email" value={c.email} empty="Sem e-mail" href={c.email && `mailto:${c.email}`} />
      <Line icon={IdCard} testid="client-document" value={c.document} empty="Sem CPF/CNPJ" />
      <Line icon={UserRound} testid="client-contact-person" value={c.contact_person} empty="Sem pessoa de contato" />
      <div className="sm:col-span-2"><Line icon={MapPin} testid="client-address" value={c.address} empty="Sem endereço" href={c.address && mapsSearch(c.address)} external /></div>
      {c.notes && <div className="sm:col-span-2"><Line icon={FileText} testid="client-notes" value={c.notes} /></div>}
      <div className="flex flex-wrap gap-2 text-[11px] font-semibold sm:col-span-2">
        <span className={`rounded-full border px-2 py-0.5 ${c.active === false ? "border-rose-500/40 text-rose-300" : "border-emerald-500/40 text-emerald-300"}`} data-testid="client-active-badge">{c.active === false ? "Inativo" : "Ativo"}</span>
        {c.email_opt_out && <span className="rounded-full border border-slate-600 px-2 py-0.5 text-slate-300" data-testid="client-optout-badge">Não aceita e-mails</span>}
      </div>
    </div>
    <div className="flex shrink-0 flex-col items-end gap-2">
      <WhatsAppButton phone={c.phone} who={c.contact_person || c.name} testid="client-whatsapp-button" />
      <Button size="sm" variant="ghost" onClick={onEdit} data-testid="client-contact-edit-button"><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
    </div>
  </div>
);

export const ClientContactCard = ({ name }) => {
  const { bump } = useServices();
  const [c, setC] = useState(blankClient());
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(null);
    api.get("/clients/contact", { params: { name } }).then((r) => setC(r.data)).catch(() => {});
  }, [name]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/clients/contact", { ...draft, name });
      setC(data);
      setDraft(null);
      toast.success("Dados do cliente salvos.");
      bump();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 rounded-xl border border-slate-700/60 bg-slate-800/60 p-4" data-testid="client-contact-card">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Dados do cliente</div>
      {!draft ? <ContactView c={c} onEdit={() => setDraft({ ...c })} /> : (
        <form onSubmit={save} className="space-y-3">
          <ClientFields c={draft} setC={setDraft} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDraft(null)} data-testid="client-contact-cancel">Cancelar</Button>
            <Button type="submit" disabled={saving} className="rounded-full" data-testid="client-contact-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
